<template>
  <div class="deposit-page">
    <div class="page-content">
      <!-- Step 1: Amount Input -->
      <div v-if="currentStep === 1" class="step-container step-amount">
        <div class="step-card">
          <div class="step-header">
            <h2 class="step-title">
              ฝากเงิน
            </h2>
          </div>

          <!-- Promotion Selection Section -->
          <div class="promotion-selection-section">
            <label class="input-label">โปรโมชั่น</label>

            <!-- If no promotion selected - show button -->
            <div
              v-if="!selectedPromotion"
              class="promo-select-button"
              @click="openPromotionModal"
            >
              <div class="promo-btn-content">
                <span class="promo-btn-icon">🎁</span>
                <div class="promo-btn-text">
                  <span class="promo-btn-title">เลือกโปรโมชั่น</span>
                  <span class="promo-btn-subtitle">หรือฝากโดยไม่รับโปร</span>
                </div>
              </div>
              <span class="promo-btn-arrow">›</span>
            </div>

            <!-- If promotion selected - show card -->
            <div v-else class="selected-promo-display">
              <div class="promo-display-content">
                <div class="promo-display-icon">
                  {{ selectedPromotion.icon || '🎁' }}
                </div>
                <div class="promo-display-info">
                  <h4 class="promo-display-title">
                    {{ selectedPromotion.title }}
                  </h4>
                  <p class="promo-display-teaser">
                    {{ selectedPromotion.teaser }}
                  </p>
                  <p class="promo-display-min">
                    ฝากขั้นต่ำ
                    {{
                      selectedPromotion.minimum_deposit ||
                        selectedPromotion.minDeposit
                    }}
                    บาท
                  </p>
                </div>
              </div>
              <div class="promo-display-actions">
                <button class="promo-change-btn" @click="openPromotionModal">
                  เปลี่ยน
                </button>
                <button
                  class="promo-cancel-btn"
                  @click="cancelPromotionInStep1"
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          </div>

          <div class="amount-input-section">
            <label class="input-label">จำนวนเงินที่ต้องการฝาก</label>
            <div class="amount-input-wrapper">
              <span class="currency-symbol">฿</span>
              <input
                v-model.number="depositAmount"
                type="number"
                class="amount-input"
                placeholder="0"
                min="0"
                @input="selectedAmount = null"
              />
            </div>
            <p class="amount-hint">
              ขั้นต่ำ
              {{
                selectedPromotion &&
                selectedPromotion.minimum_deposit > (minimumDeposit.value || 20)
                  ? selectedPromotion.minimum_deposit
                  : minimumDeposit.value || 20
              }}
              บาท
            </p>
            <div v-if="isMockCredit" class="amount-warning">
              <span class="warning-text"
                >ยืนยันบัญชีตามจำนวนเงินที่กำหนด เพื่อรับโปรเครดิตฟรี</span
              >
            </div>
          </div>

          <!-- Diamond Rewards Section -->
          <div v-if="depositAmount > 0" class="diamond-rewards-section">
            <!-- Current diamonds earned -->
            <div class="diamond-current">
              <span class="diamond-label">ได้รับ:</span>
              <span class="diamond-icon">💎</span>
              <span class="diamond-value">{{ calculatedDiamonds }}</span>
            </div>
            <!-- Upsell: deposit 300 for bonus -->
            <div
              v-if="showDiamondUpsell"
              class="diamond-upsell"
              @click="selectAmount(300)"
            >
              <span class="upsell-text"
                >เพิ่มอีก {{ 300 - depositAmount }}฿ เพื่อรับโบนัส
                <strong>100</strong> 💎</span
              >
              <span class="upsell-arrow">›</span>
            </div>
            <!-- Bonus achieved badge -->
            <div v-if="showDiamondBonus" class="diamond-bonus-badge">
              <span>🎉 โบนัสพิเศษ!</span>
            </div>
          </div>

          <div class="amount-shortcuts-section">
            <label class="input-label">เลือกจำนวนเงิน</label>
            <div class="shortcuts-grid">
              <button
                v-for="amount in amountShortcuts"
                :key="amount"
                class="shortcut-btn"
                :class="{ active: selectedAmount === amount }"
                @click="selectAmount(amount)"
              >
                {{ formatShortcutAmount(amount) }}
              </button>
            </div>
          </div>

          <button
            class="continue-btn"
            :disabled="!isValidAmount || isLoadingPayment"
            @click="confirmAmount"
          >
            <span v-if="isLoadingPayment">กำลังโหลด...</span>
            <span v-else>ดำเนินการต่อ</span>
          </button>
        </div>
      </div>

      <!-- Promotion Selection Modal -->
      <div
        v-if="showPromotionModal"
        class="promo-modal-overlay"
        @click="closePromotionModal"
      >
        <div class="promo-modal" @click.stop>
          <div class="promo-modal-header">
            <h3 class="promo-modal-title">
              เลือกโปรโมชั่น
            </h3>
            <button class="promo-modal-close" @click="closePromotionModal">
              ×
            </button>
          </div>

          <div class="promo-modal-content">
            <!-- Promotion Cards -->
            <div
              v-for="promotion in sortedPromotions"
              :key="promotion.id"
              class="promo-modal-card"
              :class="{
                'promo-event-card': promotion.type === 'event',
                'promo-register-card': promotion.type === 'register'
              }"
              @click="selectPromotion(promotion)"
            >
              <!-- Event Badge -->
              <div v-if="promotion.type === 'event'" class="event-badge">
                <span class="event-badge-text">โปรพิเศษ</span>
              </div>

              <!-- Register Badge -->
              <div v-if="promotion.type === 'register'" class="register-badge">
                <span class="register-badge-icon">⭐</span>
                <span class="register-badge-text">สมาชิกใหม่</span>
              </div>

              <div
                class="promo-icon"
                :class="{
                  'event-icon': promotion.type === 'event',
                  'register-icon': promotion.type === 'register'
                }"
              >
                {{
                  promotion.type === 'register'
                    ? '🎉'
                    : promotion.type === 'event'
                    ? '⚡'
                    : promotion.icon
                }}
              </div>
              <div class="promo-content">
                <h4
                  class="promo-title"
                  :class="{
                    'event-title': promotion.type === 'event',
                    'register-title': promotion.type === 'register'
                  }"
                >
                  {{ promotion.title }}
                </h4>
                <p class="promo-teaser">
                  {{ promotion.teaser }}
                </p>

                <!-- Register Motivational Line -->
                <div
                  v-if="promotion.type === 'register'"
                  class="register-motivational"
                >
                  <span class="register-motivational-text"
                    >ฝากครั้งแรกรับโบนัสพิเศษ!</span
                  >
                </div>

                <!-- Event Countdown Timer -->
                <div
                  v-if="
                    promotion.type === 'event' &&
                      promotion.start_date &&
                      promotion.end_date
                  "
                  class="event-time-info"
                >
                  <template v-if="isEventActive(promotion)">
                    <span class="event-countdown-compact">
                      หมดเขตใน:
                      <span v-if="getEventTimeRemaining(promotion).days > 0">
                        {{ getEventTimeRemaining(promotion).days }} วัน
                      </span>
                      {{ getEventTimeRemaining(promotion).hours }}:{{
                        getEventTimeRemaining(promotion).minutes
                      }}:{{ getEventTimeRemaining(promotion).seconds }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="event-upcoming">
                      เริ่ม {{ promotion.start_date }}
                    </span>
                  </template>
                </div>
              </div>
              <button
                class="promo-detail-btn"
                :class="{
                  'event-detail-btn': promotion.type === 'event',
                  'register-detail-btn': promotion.type === 'register'
                }"
                @click.stop="openPromoDetail(promotion)"
              >
                รายละเอียด
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Promotion Detail Modal -->
      <div
        v-if="showPromoDetailModal"
        class="promo-detail-overlay"
        @click="closePromoDetail"
      >
        <div
          class="promo-detail-modal"
          :class="{
            'event-detail-modal':
              detailPromotion && detailPromotion.type === 'event',
            'register-detail-modal':
              detailPromotion && detailPromotion.type === 'register'
          }"
          @click.stop
        >
          <div class="promo-detail-header">
            <button class="promo-detail-back" @click="closePromoDetail">
              ← กลับ
            </button>
            <h3 class="promo-detail-title">
              รายละเอียดโปรโมชั่น
            </h3>
          </div>
          <div
            v-if="detailPromotion"
            class="promo-detail-body"
            :class="{
              'event-detail-body': detailPromotion.type === 'event',
              'register-detail-body': detailPromotion.type === 'register'
            }"
          >
            <div
              class="promo-detail-icon"
              :class="{
                'event-icon-glow': detailPromotion.type === 'event',
                'register-icon-glow': detailPromotion.type === 'register'
              }"
            >
              {{
                detailPromotion.type === 'register'
                  ? '🎉'
                  : detailPromotion.icon || '🎁'
              }}
            </div>
            <h4
              class="promo-detail-name"
              :class="{
                'event-name': detailPromotion.type === 'event',
                'register-name': detailPromotion.type === 'register'
              }"
            >
              {{ detailPromotion.title }}
            </h4>
            <p class="promo-detail-teaser">
              {{ detailPromotion.teaser }}
            </p>

            <div class="promo-conditions">
              <div class="condition-row">
                <span class="condition-label">ฝากขั้นต่ำ</span>
                <span class="condition-value">
                  {{
                    detailPromotion.minimum_deposit ||
                      detailPromotion.minDeposit
                  }}
                  บาท
                </span>
              </div>
              <div v-if="detailPromotion.maximum_deposit" class="condition-row">
                <span class="condition-label">ฝากสูงสุด</span>
                <span class="condition-value">
                  {{ detailPromotion.maximum_deposit }} บาท
                </span>
              </div>
              <div
                v-if="
                  detailPromotion.turnover && detailPromotion.type !== 'event'
                "
                class="condition-row"
              >
                <span class="condition-label">ทำเทิร์น</span>
                <span class="condition-value">
                  {{ detailPromotion.turnover }} เทิร์น
                </span>
              </div>
              <div
                v-if="
                  detailPromotion.turnprofit === 0 &&
                    (detailPromotion.type === 'event' ||
                      detailPromotion.type === 'register')
                "
                class="condition-row"
              >
                <span class="condition-label">ทำยอด</span>
                <span class="condition-value"> ** ไม่ต้องทำยอด ** </span>
              </div>
              <div
                v-if="
                  detailPromotion.turnprofit > 0 &&
                    detailPromotion.type !== 'register'
                "
                class="condition-row"
              >
                <span class="condition-label">ทำยอด</span>
                <span class="condition-value">
                  {{ detailPromotion.turnprofit }} เท่า
                </span>
              </div>
              <div
                v-if="detailPromotion.max_withdraw > 0"
                class="condition-row"
              >
                <span class="condition-label">ถอนสูงสุด</span>
                <span class="condition-value">
                  {{ detailPromotion.max_withdraw }} เท่า
                </span>
              </div>
              <div
                v-if="detailPromotion.start_time && detailPromotion.end_time"
                class="condition-row"
              >
                <span class="condition-label">ช่วงเวลา</span>
                <span class="condition-value">
                  {{ detailPromotion.start_time }} -
                  {{ detailPromotion.end_time }}
                </span>
              </div>
            </div>
          </div>
          <div
            class="promo-detail-footer"
            :class="{
              'event-detail-footer':
                detailPromotion && detailPromotion.type === 'event',
              'register-detail-footer':
                detailPromotion && detailPromotion.type === 'register'
            }"
          >
            <button
              class="promo-select-btn"
              :class="{
                'event-select-btn':
                  detailPromotion && detailPromotion.type === 'event',
                'register-select-btn':
                  detailPromotion && detailPromotion.type === 'register'
              }"
              @click="selectPromotionFromDetail"
            >
              เลือกโปรโมชั่นนี้
            </button>
          </div>
        </div>
      </div>

      <!-- Step 2: Transfer Information -->
      <div v-if="currentStep === 2" class="step-container step-transfer">
        <div class="step-card">
          <!-- Countdown Timer (Compact) -->
          <div
            class="countdown-section"
            :class="{ warning: countdownTimer <= 30 }"
          >
            <span class="countdown-icon">⏱️</span>
            <span class="countdown-time">{{ formattedCountdown }}</span>
            <span class="countdown-text">กรุณาโอนภายในเวลาที่กำหนด</span>
          </div>

          <!-- Deposit Amount (non-QR: shown above; QR: shown below QR) -->
          <div v-if="!isQrPayment" class="transfer-amount-section">
            <label class="section-label">💰 ยอดที่ต้องโอน</label>
            <div class="amount-display">
              <span class="amount-value"
                >฿ {{ formatNumber(displayTransferAmount) }}</span
              >
            </div>
          </div>

          <!-- Warning: Transfer exact amount (non-QR position) -->
          <div v-if="!isQrPayment" class="deposit-warning-banner">
            <span class="deposit-warning-icon">⚠️</span>
            <span class="deposit-warning-text"
              >กรุณาฝากเงินเท่ากับยอดที่ระบุ ไม่เช่นนั้นยอดจะไม่ปรับออโต้</span
            >
          </div>

          <!-- Selected Promotion (if any) -->
          <div v-if="selectedPromotion" class="selected-promo-section">
            <label class="section-label">🎁 โปรโมชั่นที่เลือก</label>
            <div class="selected-promo-card">
              <div class="promo-info">
                <h4 class="promo-title">
                  {{ selectedPromotion.title }}
                </h4>
                <p class="promo-teaser">
                  {{ selectedPromotion.teaser }}
                </p>
              </div>
            </div>
          </div>

          <!-- QR Code Payment Section -->
          <div v-if="isQrPayment" class="qr-section">
            <p class="qr-hint-top">
              📱 โปรดสแกน QR Code ด้านล่าง
              ด้วยแอปพลิเคชันธนาคารของคุณเพื่อดำเนินการชำระเงิน
            </p>

            <div class="qr-image-frame">
              <img
                :src="paymentData.payment_image_base64"
                alt="QR Code สำหรับชำระเงิน"
                class="qr-image"
              />
            </div>

            <button class="save-qr-btn" @click="saveQrCode">
              💾 บันทึก QR Code
            </button>

            <p class="qr-hint-bottom">
              💬 QR Code นี้สามารถทำรายการได้ครั้งเดียวเท่านั้น
            </p>
          </div>

          <!-- Deposit Amount (QR position: below QR) -->
          <div v-if="isQrPayment" class="transfer-amount-section">
            <label class="section-label">💰 ยอดที่ต้องโอน</label>
            <div class="amount-display">
              <span class="amount-value"
                >฿ {{ formatNumber(displayTransferAmount) }}</span
              >
            </div>
          </div>

          <!-- Warning: Transfer exact amount (QR position) -->
          <div v-if="isQrPayment" class="deposit-warning-banner">
            <span class="deposit-warning-icon">⚠️</span>
            <span class="deposit-warning-text"
              >กรุณาฝากเงินเท่ากับยอดที่ระบุ ไม่เช่นนั้นยอดจะไม่ปรับออโต้</span
            >
          </div>

          <!-- User Bank Account (From) -->
          <div v-if="!isQrPayment" class="bank-section from-bank">
            <label class="section-label">📤 โอนจากบัญชี</label>
            <div class="bank-card user-bank">
              <div class="bank-icon">
                <feather-icon icon="CreditCardIcon" size="32" />
              </div>
              <div class="bank-info">
                <div class="bank-name">
                  {{ member && member.name_th }}
                </div>
                <div class="account-name">
                  {{
                    member
                      ? `${member.firstname} ${member.lastname}`
                      : 'Loading...'
                  }}
                </div>
                <div class="account-number-row">
                  <span class="account-number">{{
                    member ? member.bank_no : '•••-•-•••••-•'
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Company Bank Account (To) -->
          <div v-if="!isQrPayment" class="bank-section to-bank">
            <label class="section-label">📥 โอนเข้าบัญชี</label>
            <div class="bank-card company-bank">
              <div class="bank-logo">
                <img
                  :src="
                    paymentData
                      ? bankLogoMap[paymentData.payee_bank_code]
                      : bankInfo && bankInfo.logoSrc
                  "
                  :alt="
                    paymentData
                      ? `ธนาคาร${paymentData.payee_bank_name_th ||
                          paymentData.payee_bank_code}`
                      : bankInfo && bankInfo.altText
                  "
                  class="logo-img"
                />
              </div>
              <div class="bank-info">
                <div class="bank-name">
                  {{
                    paymentData
                      ? `ธนาคาร${paymentData.payee_bank_name_th ||
                          paymentData.payee_bank_code}`
                      : bankInfo && bankInfo.displayName
                  }}
                </div>
                <div class="account-name">
                  {{
                    paymentData
                      ? paymentData.payee_bank_account_fullname
                      : bank
                      ? bank.bank_account_name
                      : 'Loading...'
                  }}
                </div>
                <div class="account-number-row">
                  <span class="account-number">{{
                    paymentData
                      ? paymentData.payee_bank_account_no
                      : bank
                      ? bank.bank_account_no
                      : '•••-•-•••••-•'
                  }}</span>
                  <button
                    class="copy-btn"
                    :disabled="isCopying"
                    :class="{ copied: hasCopied }"
                    @click="copyAccountNumber"
                  >
                    {{ isCopying ? '⏳' : hasCopied ? '✅' : '📋' }}
                    {{
                      isCopying
                        ? 'กำลังคัดลอก...'
                        : hasCopied
                        ? 'คัดลอกแล้ว'
                        : 'คัดลอก'
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button class="confirm-transfer-btn" @click="confirmTransfer">
              ✅ โอนเงินเรียบร้อยแล้ว
            </button>
            <button class="cancel-btn" @click="cancelDeposit">
              ยกเลิกรายการ
            </button>
          </div>
        </div>
      </div>

      <!-- Step 3: Success State -->
      <div v-if="currentStep === 3" class="step-container step-success">
        <div class="step-card success-card">
          <!-- Success Icon -->
          <div class="success-icon-wrapper">
            <div class="success-icon">
              🎉
            </div>
          </div>

          <!-- Thank You Message -->
          <div class="success-message">
            <h3 class="success-title">
              กรุณารอสักครู่ระบบกำลังปรับยอดให้อัตโนมัติ<br />
              หากโอนเงินแล้ว อย่าเพิ่งสร้างรายการใหม่นะคะ<br />
              ขอให้ เฮงๆ และเป็นวันที่ดี
            </h3>
          </div>

          <!-- Info Message -->
          <div class="success-info">
            <p class="info-text">
              หากยอดเงินไม่เข้าภายใน 2 นาที<br />
              สามารถติดต่อแอดมินได้ 24 ชั่วโมง
            </p>
          </div>

          <!-- Exit Button with Auto-close Timer -->
          <button class="exit-btn" @click="startNewDeposit">
            ออกจากหน้านี้ ({{ successAutoCloseSeconds }}s)
          </button>
        </div>
      </div>

      <!-- Transaction History Section (Step 1 only) -->
      <div v-if="currentStep === 1" class="transaction-section">
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title">
              📊 รายการฝากล่าสุด
            </h3>
          </div>

          <div class="transaction-list">
            <div v-if="items.length === 0" class="empty-state">
              <span class="empty-icon">📄</span>
              <p class="empty-text">
                ยังไม่มีรายการฝากเงิน
              </p>
            </div>

            <div v-else class="transaction-items">
              <div
                v-for="(item, index) in items"
                :key="item.Tx"
                class="transaction-item"
              >
                <div class="transaction-index">
                  {{ index + 1 }}
                </div>
                <div class="transaction-info">
                  <div class="transaction-amount">
                    {{ formatNumber(item.amount) }} บาท
                  </div>
                  <div class="transaction-date">
                    {{ item.date_time }}
                  </div>
                </div>
                <div
                  class="transaction-status"
                  :class="getStatusClass(item.status.variant)"
                >
                  {{ getStatusText(item.status.title) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getUserData } from '@/auth/utils'
import getCurrentDomain from '@/utils/domain'
import ktbLogo from '@/assets/images/logo/ktb-logo.png'
import scbLogo from '@/assets/images/logo/scb-logo.png'
import gsbLogo from '@/assets/images/logo/gsb-logo.png'
import bblLogo from '@/assets/images/logo/bbl-logo.png'
import kbankLogo from '@/assets/images/logo/kbank-logo.png'
import bayLogo from '@/assets/images/logo/bay-logo.png'
import ttbLogo from '@/assets/images/logo/ttb-logo.png'

const bankLogoMap = {
  GSB: gsbLogo,
  BBL: bblLogo,
  KBANK: kbankLogo,
  BAY: bayLogo,
  TTB: ttbLogo,
  TMB: ttbLogo,
  KTB: ktbLogo,
  SCB: scbLogo
}

export default {
  name: 'DepositPage',
  components: {
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent
  },
  data() {
    return {
      bankLogoMap,

      // Step management
      currentStep: 1, // 1: amount with promotion selection, 2: transfer
      showPromotionModal: false, // NEW: Modal visibility state

      // Amount data
      depositAmount: 0,
      selectedAmount: null,
      amountShortcuts: [20, 50, 100, 200, 500, 1000, 2000, 5000],

      // Promotion data
      wantPromotion: null,
      selectedPromotion: null,
      currentPromotion: null, // Store current active promotion from API

      // Countdown timer
      countdownTimer: 180,
      timerInterval: null,

      // API data
      accountNo: '',
      items: [],
      user: null,
      bank: null,
      member: null,
      minimumDeposit: { value: 20 },
      minimumWithdraw: { value: 0 },
      depositMethod: 'local',
      minimumDepositPayment: 88,
      isLoadingPayment: false,
      paymentData: null,

      // Copy button states
      isCopying: false,
      hasCopied: false,
      copyTimeout: null,

      // Promotions from API
      promotions: [],

      // Promotion detail modal
      showPromoDetailModal: false,
      detailPromotion: null,

      // Event promotion countdown
      eventCountdownInterval: null,
      eventCountdownTick: 0,

      // LINE Official URLs (to be configured)
      adminLineUrl: 'https://lin.ee/UulNYYy', // Placeholder
      notificationBotUrl: 'https://lin.ee/UulNYYy', // Placeholder

      // Success state auto-close timer
      successAutoCloseTimer: null,
      successAutoCloseSeconds: 60,

      // Deposit polling state
      depositPollingInterval: null,
      depositPollingTimeout: null,
      depositPollingCount: 0,
      maxPollingAttempts: 5,
      pollingIntervalMs: 10000, // 10 seconds
      firstPollDelayMs: 3000, // 3 seconds delay for first poll
      lastKnownTxId: null
    }
  },
  computed: {
    ...mapGetters('member', ['isMockCredit', 'promoMinimumDeposit']),
    bankInfo() {
      if (!this.bank) return null

      const isKTB = this.bank.bank_id === 3
      return {
        logoFile: isKTB ? 'ktb-logo.png' : 'scb-logo.png',
        logoSrc: isKTB ? ktbLogo : scbLogo,
        altText: isKTB ? 'ธนาคารกรุงไทย' : 'ธนาคารไทยพาณิชย์',
        displayName: isKTB ? 'ธนาคารกรุงไทย' : 'ธนาคารไทยพาณิชย์'
      }
    },
    memberBankInfo() {
      // Mock bank info for member - in real app, get from member data
      return {
        bankName: 'ธนาคารกสิกรไทย'
      }
    },
    isValidAmount() {
      let min = this.minimumDeposit.value || 20

      // Check if in mock credit state
      if (this.isMockCredit && this.promoMinimumDeposit > 0) {
        min = this.promoMinimumDeposit
      }

      return this.depositAmount >= min
    },
    formattedCountdown() {
      const minutes = Math.floor(this.countdownTimer / 60)
      const seconds = this.countdownTimer % 60
      return `${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    },
    calculatedDiamonds() {
      if (!this.depositAmount || this.depositAmount <= 0) return 0
      // Bonus: 100 diamonds for exactly 300 baht (without promotion)
      if (this.depositAmount === 300 && !this.selectedPromotion) {
        return 100
      }
      // Regular: 10 baht = 1 diamond
      return Math.floor(this.depositAmount / 10)
    },
    showDiamondUpsell() {
      return (
        this.depositAmount > 0 &&
        this.depositAmount < 300 &&
        !this.selectedPromotion
      )
    },
    showDiamondBonus() {
      return this.depositAmount === 300 && !this.selectedPromotion
    },
    isQrPayment() {
      return this.paymentData?.payment_method === 'qr'
    },
    displayTransferAmount() {
      return this.paymentData?.customer_transfer_amount ?? this.depositAmount
    },
    // Sort promotions: register first, then event, then daily
    sortedPromotions() {
      if (!this.promotions || !this.promotions.length) return []
      const priority = { register: 0, event: 1 }
      return [...this.promotions].sort((a, b) => {
        const aPriority = priority[a.type] !== undefined ? priority[a.type] : 2
        const bPriority = priority[b.type] !== undefined ? priority[b.type] : 2
        return aPriority - bPriority
      })
    }
  },

  async beforeMount() {
    this.user = getUserData()

    // Parallelize independent API calls for faster page load (60-80% improvement)
    const [, , , member, currentPromo] = await Promise.all([
      this.getActiveBank(),
      this.getTransactions(),
      this.getPromotions(),
      this.getMemberInfo(),
      this.getCurrentPromotion()
    ])

    this.$eventBus.$emit('refreshBalance')
    this.member = member

    // Check if user has a current active promotion
    if (currentPromo && currentPromo.promotion_id) {
      this.currentPromotion = currentPromo
      // Auto-select the matching promotion if it exists
      const matchedPromotion = this.promotions.find(
        promo => promo.id === currentPromo.promotion_id
      )
      if (matchedPromotion) {
        this.selectedPromotion = matchedPromotion
        this.wantPromotion = true
      }
    }

    // If in mock state, force deposit amount to promoMinimumDeposit
    if (this.isMockCredit && this.promoMinimumDeposit > 0) {
      this.depositAmount = this.promoMinimumDeposit
    }
  },

  beforeDestroy() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout)
    }

    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }

    // Clear success auto-close timer
    if (this.successAutoCloseTimer) {
      clearInterval(this.successAutoCloseTimer)
    }

    // Stop deposit polling
    this.stopDepositPolling()

    // Stop event countdown
    this.stopEventCountdown()
  },
  methods: {
    // Amount selection
    selectAmount(amount) {
      this.selectedAmount = amount
      this.depositAmount = amount
    },

    formatShortcutAmount(amount) {
      if (amount >= 1000) {
        return `${amount / 1000}K`
      }
      return amount.toString()
    },

    formatNumber(num) {
      if (!num) return '0'
      return new Intl.NumberFormat('th-TH').format(num)
    },

    // Promotion modal methods
    openPromotionModal() {
      this.showPromotionModal = true
      this.startEventCountdown()
    },

    closePromotionModal() {
      this.showPromotionModal = false
      this.stopEventCountdown()
    },

    // Event promotion countdown methods
    // API format: "YYYY-MM-DD HH:mm:ss"
    isEventActive(promotion) {
      if (!promotion.start_date || !promotion.end_date) return false
      const now = new Date()
      const startDate = new Date(promotion.start_date.replace(' ', 'T'))
      const endDate = new Date(promotion.end_date.replace(' ', 'T'))
      return now >= startDate && now <= endDate
    },

    getEventTimeRemaining(promotion) {
      // Reference tick to trigger Vue reactivity
      // eslint-disable-next-line no-unused-expressions
      this.eventCountdownTick

      if (!promotion.end_date) {
        return { days: 0, hours: '00', minutes: '00', seconds: '00' }
      }
      const now = new Date()
      // Parse "YYYY-MM-DD HH:mm:ss" format
      const endDate = new Date(promotion.end_date.replace(' ', 'T'))

      // Check if date is valid
      if (Number.isNaN(endDate.getTime()) || now >= endDate) {
        return { days: 0, hours: '00', minutes: '00', seconds: '00' }
      }

      const diffMs = endDate - now
      const diffSeconds = Math.floor(diffMs / 1000)
      const days = Math.floor(diffSeconds / 86400)
      const hours = Math.floor((diffSeconds % 86400) / 3600)
      const minutes = Math.floor((diffSeconds % 3600) / 60)
      const seconds = diffSeconds % 60

      return {
        days,
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0')
      }
    },

    startEventCountdown() {
      this.stopEventCountdown()
      // Only run interval when modal is visible and has event promotions
      const hasEventPromo = this.promotions.some(p => p.type === 'event')
      if (!hasEventPromo) return

      this.eventCountdownInterval = setInterval(() => {
        this.eventCountdownTick += 1
      }, 1000)
    },

    stopEventCountdown() {
      if (this.eventCountdownInterval) {
        clearInterval(this.eventCountdownInterval)
        this.eventCountdownInterval = null
      }
    },

    // Promotion detail modal methods
    openPromoDetail(promotion) {
      this.detailPromotion = promotion
      this.showPromoDetailModal = true
    },

    closePromoDetail() {
      this.showPromoDetailModal = false
      this.detailPromotion = null
    },

    selectPromotionFromDetail() {
      if (this.detailPromotion) {
        this.selectPromotion(this.detailPromotion)
        this.closePromoDetail()
      }
    },

    selectNoPromotion() {
      this.wantPromotion = false
      this.selectedPromotion = null
      this.closePromotionModal()
    },

    async cancelPromotionInStep1() {
      // Cancel promotion without confirmation (in Step 1)
      try {
        await this.$http.delete('/member/promotion')

        // Successfully deleted promotion
        this.selectedPromotion = null
        this.wantPromotion = false

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'ยกเลิกโปรโมชั่นแล้ว',
            text: 'คุณสามารถฝากเงินตามปกติได้',
            icon: 'CheckCircleIcon',
            variant: 'success'
          }
        })
      } catch (error) {
        console.error('Error canceling promotion:', error)

        // Extract error message from response or use default
        const errorMessage =
          error.response?.data?.message ||
          'ไม่สามารถยกเลิกโปรโมชั่นได้ กรุณาลองใหม่อีกครั้ง'

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },

    // Step navigation
    async confirmAmount() {
      if (!this.isValidAmount) {
        return
      }

      // Check if user selected a promotion
      if (this.selectedPromotion) {
        // Check if current credit is greater than 10
        const currentCredit = this.$store.getters['member/displayBalance']
        if (currentCredit > 10) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'ไม่สามารถรับโปรโมชั่นได้',
              text:
                'คุณมียอดเครดิตเกิน 10 บาท กรุณาใช้เครดิตให้เหลือน้อยกว่า 10 บาทก่อนรับโปรโมชั่น',
              icon: 'AlertCircleIcon',
              variant: 'warning'
            }
          })
          return
        }

        // Check if deposit amount is below minimum for this promotion
        if (
          this.selectedPromotion.minimum_deposit &&
          this.depositAmount < this.selectedPromotion.minimum_deposit
        ) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'ยอดฝากไม่ถึงขั้นต่ำที่กำหนด',
              text: `โปรโมชั่นนี้รับยอดฝากขึ้นต่ำ ${this.selectedPromotion.minimum_deposit} บาท`,
              icon: 'AlertCircleIcon',
              variant: 'warning'
            }
          })
          return
        }

        // Check if deposit amount exceeds maximum for this promotion
        if (
          this.selectedPromotion.maximum_deposit &&
          this.depositAmount > this.selectedPromotion.maximum_deposit
        ) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'ยอดฝากเกินจำนวนที่กำหนด',
              text: `โปรโมชั่นนี้รับยอดฝากสูงสุด ${this.selectedPromotion.maximum_deposit} บาท`,
              icon: 'AlertCircleIcon',
              variant: 'warning'
            }
          })
          return
        }
      }

      // If depositMethod is not 'local', fetch payment data from payment API
      if (
        this.depositMethod !== 'local' &&
        this.depositAmount > this.minimumDepositPayment
      ) {
        this.isLoadingPayment = true
        try {
          const resp = await this.$http.post('/member/payment/deposit', {
            amount: this.depositAmount
          })
          if (resp.data && resp.data.success && resp.data.data) {
            this.paymentData = resp.data.data
          }
          // If not success, paymentData stays null → fall back to bank info
        } catch (error) {
          // On error, fall back to getActiveBank data (already in this.bank)
        } finally {
          this.isLoadingPayment = false
        }
      }

      this.currentStep = 2
      this.startCountdown()
    },

    async selectPromotion(promotion) {
      // Check if promotion has time restrictions
      if (promotion.start_time && promotion.end_time) {
        const now = new Date()
        const currentTime = `${String(now.getHours()).padStart(
          2,
          '0'
        )}:${String(now.getMinutes()).padStart(2, '0')}:${String(
          now.getSeconds()
        ).padStart(2, '0')}`

        if (
          currentTime < promotion.start_time ||
          currentTime > promotion.end_time
        ) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'โปรโมชั่นนี้ไม่อยู่ในช่วงเวลาที่กำหนด',
              text: `เวลารับโปรโมชั่น: ${promotion.start_time} - ${promotion.end_time}`,
              icon: 'AlertCircleIcon',
              variant: 'warning'
            }
          })
          return
        }
      }

      // Store the selected promotion via API
      try {
        await this.$http.post('/member/promotion', {
          promoId: promotion.id
        })

        // Successfully stored promotion selection
        this.selectedPromotion = promotion
        this.wantPromotion = true
        this.closePromotionModal()

        // Show success toast
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เลือกโปรโมชั่นสำเร็จ',
            text: `คุณได้เลือก ${promotion.title}`,
            icon: 'CheckCircleIcon',
            variant: 'success'
          }
        })
      } catch (error) {
        console.error('Error selecting promotion:', error)

        // Extract error message from response or use default
        const errorMessage =
          error.response?.data?.message ||
          'ไม่สามารถเลือกโปรโมชั่นได้ กรุณาลองใหม่อีกครั้ง'

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },

    async changePromotion() {
      // Show confirmation dialog
      // eslint-disable-next-line no-alert
      if (
        window.confirm(
          'เปลี่ยนโปรโมชั่นจะทำให้ต้องเริ่มการฝากใหม่ คุณแน่ใจหรือไม่?'
        )
      ) {
        // Call DELETE API to cancel current promotion
        try {
          await this.$http.delete('/member/promotion')

          // Pause timer
          if (this.timerInterval) {
            clearInterval(this.timerInterval)
          }

          // Clear promotion state
          this.selectedPromotion = null
          this.wantPromotion = false

          // Show success toast
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'ยกเลิกโปรโมชั่นแล้ว',
              text: 'กรุณาเลือกโปรโมชั่นใหม่',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })

          // Go back to Step 1 and open modal
          this.currentStep = 1
          this.openPromotionModal()
        } catch (error) {
          console.error('Error canceling promotion:', error)

          // Extract error message from response or use default
          const errorMessage =
            error.response?.data?.message ||
            'ไม่สามารถยกเลิกโปรโมชั่นได้ กรุณาลองใหม่อีกครั้ง'

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'เกิดข้อผิดพลาด',
              text: errorMessage,
              icon: 'AlertCircleIcon',
              variant: 'danger'
            }
          })
        }
      }
    },

    async cancelPromotion() {
      // Show confirmation dialog
      // eslint-disable-next-line no-alert
      if (window.confirm('คุณแน่ใจหรือไม่ที่จะยกเลิกโปรโมชั่นนี้?')) {
        try {
          await this.$http.delete('/member/promotion')

          // Successfully deleted promotion
          this.selectedPromotion = null
          this.wantPromotion = false

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'ยกเลิกโปรโมชั่นแล้ว',
              text: 'คุณสามารถฝากเงินตามปกติได้',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })
        } catch (error) {
          console.error('Error canceling promotion:', error)

          // Extract error message from response or use default
          const errorMessage =
            error.response?.data?.message ||
            'ไม่สามารถยกเลิกโปรโมชั่นได้ กรุณาลองใหม่อีกครั้ง'

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'เกิดข้อผิดพลาด',
              text: errorMessage,
              icon: 'AlertCircleIcon',
              variant: 'danger'
            }
          })
        }
      }
    },

    goBack() {
      if (this.currentStep === 2) {
        // Clear timer when going back from transfer page
        if (this.timerInterval) {
          clearInterval(this.timerInterval)
        }
        this.currentStep = 1
      }
    },

    // Countdown timer
    startCountdown() {
      this.countdownTimer = this.isQrPayment ? 600 : 180

      if (this.timerInterval) {
        clearInterval(this.timerInterval)
      }

      this.timerInterval = setInterval(() => {
        if (this.countdownTimer > 0) {
          this.countdownTimer -= 1
        } else {
          this.handleTimerExpired()
        }
      }, 1000)
    },

    handleTimerExpired() {
      clearInterval(this.timerInterval)
      this.timerInterval = null

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'หมดเวลา กรุณาทำรายการใหม่',
          icon: 'AlertCircleIcon',
          variant: 'warning'
        }
      })

      // Reset to step 1
      this.resetDeposit()
    },

    // Actions
    confirmTransfer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
      }

      // Store last known transaction ID and start polling for new deposits
      this.lastKnownTxId = this.items.length > 0 ? this.items[0].Tx : null
      this.startDepositPolling()

      // Refresh transactions
      this.getTransactions()

      // Navigate to success state (Step 3)
      this.currentStep = 3

      // Start auto-close timer
      this.startSuccessAutoClose()
    },

    startSuccessAutoClose() {
      // Clear any existing timer
      if (this.successAutoCloseTimer) {
        clearInterval(this.successAutoCloseTimer)
      }

      // Reset countdown
      this.successAutoCloseSeconds = 60

      // Start countdown timer
      this.successAutoCloseTimer = setInterval(() => {
        this.successAutoCloseSeconds -= 1

        if (this.successAutoCloseSeconds <= 0) {
          this.startNewDeposit()
        }
      }, 1000)
    },

    startNewDeposit() {
      // Clear auto-close timer
      if (this.successAutoCloseTimer) {
        clearInterval(this.successAutoCloseTimer)
        this.successAutoCloseTimer = null
      }

      // Reset deposit and return to Step 1
      this.resetDeposit()
    },

    cancelDeposit() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'ยกเลิกรายการแล้ว',
          icon: 'XCircleIcon',
          variant: 'warning'
        }
      })

      // Reload current URL to reset the page and fetch current promotion
      this.resetDeposit()
    },

    async resetDeposit() {
      // Clear timer
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }

      // Reset state
      this.currentStep = 1
      this.depositAmount = 0
      this.selectedAmount = null
      this.wantPromotion = null
      this.selectedPromotion = null
      this.countdownTimer = 180
      this.showPromotionModal = false
      this.paymentData = null

      // Re-fetch current promotion from server
      const currentPromo = await this.getCurrentPromotion()
      if (currentPromo && currentPromo.promotion_id) {
        this.currentPromotion = currentPromo
        // Auto-select the matching promotion if it exists
        const matchedPromotion = this.promotions.find(
          promo => promo.id === currentPromo.promotion_id
        )
        if (matchedPromotion) {
          this.selectedPromotion = matchedPromotion
          this.wantPromotion = true
        }
      }

      // Refresh transactions and balance
      await this.getTransactions()
      this.$eventBus.$emit('refreshBalance')
    },

    // Copy functionality
    async copyAccountNumber() {
      const textToCopy = this.paymentData
        ? this.paymentData.payee_bank_account_no
        : this.bank?.bank_account_no

      if (!textToCopy) {
        this.onError('ไม่พบเลขที่บัญชี')
        return
      }

      this.isCopying = true
      this.hasCopied = false

      if (this.copyTimeout) {
        clearTimeout(this.copyTimeout)
      }

      try {
        await this.$copyText(textToCopy)
        this.onCopySuccess(textToCopy)
      } catch (err) {
        console.error('Copy failed:', err)
        this.onError('ไม่สามารถคัดลอกได้')
      } finally {
        this.isCopying = false
      }
    },

    onCopySuccess(copiedText) {
      this.hasCopied = true

      this.$toast({
        component: ToastificationContent,
        props: {
          title: `คัดลอกเลขที่บัญชี "${copiedText}" เรียบร้อยแล้ว`,
          icon: 'BellIcon',
          variant: 'success'
        }
      })

      this.copyTimeout = setTimeout(() => {
        this.hasCopied = false
      }, 3000)
    },

    async saveQrCode() {
      if (!this.paymentData || !this.paymentData.payment_image_base64) return

      const shortUuid = this.paymentData.uuid
        ? this.paymentData.uuid.slice(0, 8)
        : 'qr'
      const filename = `qr-${shortUuid}-${this.paymentData.customer_transfer_amount}.png`
      const isInAppBrowser = /Line\/|FBAN|FBAV|Instagram|Messenger/i.test(
        navigator.userAgent
      )
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

      let file = null
      try {
        const resp = await fetch(this.paymentData.payment_image_base64)
        const blob = await resp.blob()
        file = new File([blob], filename, { type: blob.type || 'image/png' })
      } catch (e) {
        // fall through — file stays null, we'll use the new-tab fallback
      }

      // Tier 1: Web Share API Level 2 — opens the native share sheet so the
      // user can tap "Save Image" and land directly in Photos/Gallery (the
      // place bank apps look when they say "Scan QR from gallery").
      if (
        file &&
        navigator.canShare &&
        navigator.canShare({ files: [file] }) &&
        !isInAppBrowser
      ) {
        try {
          await navigator.share({ files: [file], title: 'QR Code' })
          return
        } catch (e) {
          if (e && e.name === 'AbortError') return // user dismissed the sheet
          // any other failure → fall through to the next tier
        }
      }

      // Tier 2: desktop — <a download> into the Downloads folder is correct.
      if (!isMobile && file) {
        const url = URL.createObjectURL(file)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
        return
      }

      // Tier 3: mobile fallback (in-app browsers, older OS) — open the image
      // in a new tab and tell the user to long-press → "Save Image", which on
      // iOS/Android writes to Photos/Gallery (not Downloads).
      window.open(this.paymentData.payment_image_base64, '_blank')
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'กดค้างที่รูป แล้วเลือก "บันทึกภาพ" เพื่อบันทึกลงในรูปภาพ',
          icon: 'InfoIcon',
          variant: 'info'
        }
      })
    },

    onError(message = 'ไม่สามารถคัดลอกข้อมูลได้') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger'
        }
      })
    },

    // API calls
    async getActiveBank() {
      const b = await this.$http.post('/member/bank/active-rotate', {
        domain: getCurrentDomain()
      })
      this.bank = b.data.bank
      this.minimumDeposit = b.data.minDeposit || { value: 20 }
      this.minimumWithdraw = b.data.minWithdraw || { value: 0 }
      this.depositMethod = b.data.depositMethod || 'local'
      this.minimumDepositPayment = b.data.minimumDepositPayment || 88
    },

    async getMemberInfo() {
      const response = await this.$http.get('member/personal/info')
      if (response.status === 200 && response.data.member) {
        return response.data.member
      }
      return null
    },

    async getPromotions() {
      try {
        const response = await this.$http.post(
          '/member/agent/promotions/type',
          {
            domain: getCurrentDomain(),
            type: 'daily'
          }
        )
        this.promotions = response.data.map(promo => ({
          id: promo.id,
          icon: '🎁',
          title: promo.title,
          teaser: promo.description || '',
          minDeposit: promo.minimum_deposit,
          ...promo
        }))
      } catch (error) {
        console.error('Failed to fetch promotions:', error)
        this.promotions = []
      }
    },

    async getCurrentPromotion() {
      try {
        const response = await this.$http.get('/member/current/promotion')
        if (response.status === 200 && response.data.promotion) {
          return response.data.promotion
        }
        return null
      } catch (error) {
        console.error('Failed to fetch current promotion:', error)
        return null
      }
    },

    transformT(txs) {
      const resp = []
      txs.forEach(element => {
        let v = 'light-warning'
        if (element.status === 'confirmed') {
          v = 'light-success'
        } else if (element.status === 'reject') {
          v = 'light-danger'
        }

        resp.push({
          Tx: element.id,
          date_time: `${element.date} ${element.time}`,
          amount: element.amount,
          status: {
            title: element.status,
            variant: v
          }
        })
      })
      return resp
    },

    async getTransactions() {
      const tx = await this.$http.get('/member/deposit/confirm/tx')
      if (tx.status === 200) {
        const res = this.transformT(tx.data)
        this.items = res
      }
    },

    getStatusClass(variant) {
      switch (variant) {
        case 'light-success':
          return 'status-success'
        case 'light-danger':
          return 'status-danger'
        case 'light-warning':
        default:
          return 'status-warning'
      }
    },

    getStatusText(status) {
      switch (status) {
        case 'confirmed':
          return 'สำเร็จ'
        case 'reject':
          return 'ปฏิเสธ'
        default:
          return 'รอดำเนินการ'
      }
    },

    // Deposit Polling Methods
    startDepositPolling() {
      this.stopDepositPolling()
      this.depositPollingCount = 0

      // First poll after 3 second delay
      this.depositPollingTimeout = setTimeout(() => {
        this.depositPollingCount += 1
        this.checkForNewDeposit()

        // Then continue polling every 20 seconds
        this.depositPollingInterval = setInterval(() => {
          this.depositPollingCount += 1
          this.checkForNewDeposit()

          if (this.depositPollingCount >= this.maxPollingAttempts) {
            this.stopDepositPolling()
          }
        }, this.pollingIntervalMs)
      }, this.firstPollDelayMs)
    },

    stopDepositPolling() {
      if (this.depositPollingTimeout) {
        clearTimeout(this.depositPollingTimeout)
        this.depositPollingTimeout = null
      }
      if (this.depositPollingInterval) {
        clearInterval(this.depositPollingInterval)
        this.depositPollingInterval = null
      }
      this.depositPollingCount = 0
    },

    async checkForNewDeposit() {
      try {
        console.log('[Deposit Polling] Checking for new deposit...', {
          attempt: this.depositPollingCount,
          lastKnownTxId: this.lastKnownTxId
        })

        const tx = await this.$http.get('/member/deposit/confirm/tx')
        console.log('[Deposit Polling] API response:', {
          status: tx.status,
          dataLength: tx.data?.length,
          latestTx: tx.data?.[0]
        })

        if (tx.status === 200 && tx.data.length > 0) {
          const latestTx = tx.data[0]

          console.log('[Deposit Polling] Comparing:', {
            latestTxId: latestTx.id,
            lastKnownTxId: this.lastKnownTxId,
            latestTxStatus: latestTx.status,
            isNewTx: latestTx.id !== this.lastKnownTxId,
            isConfirmed: latestTx.status === 'confirmed'
          })

          // Check if this is a new confirmed transaction
          if (
            latestTx.id !== this.lastKnownTxId &&
            latestTx.status === 'confirmed'
          ) {
            console.log(
              '[Deposit Polling] New confirmed deposit detected!',
              JSON.stringify(latestTx)
            )
            this.showDepositSuccessNotification(latestTx)
            this.stopDepositPolling()

            // Refresh transaction list and balance
            this.items = this.transformT(tx.data)
            this.$eventBus.$emit('refreshBalance')
          }
        }
      } catch (error) {
        console.error('[Deposit Polling] Error:', error)
      }
    },

    showDepositSuccessNotification(transaction) {
      const amount = this.formatNumber(transaction.amount)

      this.$swal({
        html: `
          <div class="congrats-content">
            <div class="congrats-icon">🎊</div>
            <h2 class="congrats-title">ปรับยอดเรียบร้อย</h2>
            <p class="congrats-amount">+${amount} บาท</p>
          </div>
        `,
        showCloseButton: false,
        confirmButtonText: 'เข้าเล่น',
        customClass: {
          popup: 'congrats-popup',
          confirmButton: 'congrats-btn',
          closeButton: 'congrats-close'
        },
        buttonsStyling: false,
        allowOutsideClick: false
      }).then(result => {
        if (result.isConfirmed) {
          this.$router.push({ name: 'home-v3' })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.deposit-page {
  width: 100%;
  min-height: 100vh;
  padding: 10px;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (min-width: 768px) {
    padding: 20px;
  }
}

.page-content {
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

// Step containers
.step-container {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Step card base styles
.step-card,
.section-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 16px;
  margin-bottom: 12px;

  @media (min-width: 768px) {
    padding: 20px;
  }
}

// Step header
.step-header {
  text-align: center;
  margin-bottom: 20px;

  &.with-back {
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
  }
}

.header-back-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #fff;
  width: 36px;
  height: 36px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

.step-title {
  color: #ffffff;
  font-size: 20px;
  font-weight: 400;
  margin: 0;

  @media (max-width: 480px) {
    font-size: 18px;
  }
}

.step-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  margin: 6px 0 0 0;
}

// Input styles
.input-label {
  display: block;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  margin-bottom: 8px;
}

.section-label {
  display: block;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
}

// Amount input section
.amount-input-section {
  margin-bottom: 20px;
}

.amount-input-wrapper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(var(--theme-secondary-rgb), 0.3);
  border-radius: 10px;
  padding: 0 12px;
  transition: border-color 0.2s ease;
  overflow: hidden;
  max-width: 100%;

  &:focus-within {
    border-color: var(--theme-secondary);
  }
}

.currency-symbol {
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
  margin-right: 8px;
}

.amount-input {
  flex: 1;
  min-width: 0;
  width: 100%;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  padding: 12px 0;
  text-align: right;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
  }

  // Hide number input spinners
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  @media (max-width: 360px) {
    font-size: 20px;
  }
}

.amount-hint {
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  margin: 6px 0 0 0;
  text-align: right;
}

.amount-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0 0 0;
  padding: 12px 14px;
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.18) 0%,
    rgba(255, 87, 34, 0.12) 100%
  );
  border: 1px solid rgba(220, 53, 69, 0.5);
  border-left: 4px solid #dc3545;
  border-radius: 8px;
  animation: warningPulse 1.5s ease-in-out infinite;

  .warning-icon {
    font-size: 20px;
    flex-shrink: 0;
  }

  .warning-text {
    color: #ff6b6b;
    font-size: 13px;
    font-weight: 300;
    line-height: 1.4;
  }
}

@keyframes warningPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4);
  }
  50% {
    box-shadow: 0 0 15px 3px rgba(220, 53, 69, 0.25);
  }
}

// Diamond Rewards Section (Compact)
.diamond-rewards-section {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: rgba(0, 191, 255, 0.06);
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 8px;
}

.diamond-current {
  display: flex;
  align-items: center;
  gap: 5px;

  .diamond-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
  }

  .diamond-icon {
    font-size: 14px;
  }

  .diamond-value {
    font-size: 16px;
    font-weight: 700;
    color: #00bfff;
  }
}

.diamond-upsell {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 5px 10px;
  background: linear-gradient(
    135deg,
    rgba(var(--theme-secondary-rgb), 0.2) 0%,
    rgba(255, 153, 0, 0.15) 100%
  );
  border: 1px solid rgba(var(--theme-secondary-rgb), 0.4);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(var(--theme-secondary-rgb), 0.3) 0%,
      rgba(255, 153, 0, 0.2) 100%
    );
    transform: scale(1.02);
  }

  .upsell-text {
    color: #ffffff;
    font-size: 11px;

    strong {
      font-weight: 700;
    }
  }

  .upsell-arrow {
    color: #ffffff;
    font-size: 14px;
    font-weight: 300;
  }
}

.diamond-bonus-badge {
  margin-left: auto;
  padding: 4px 10px;
  background: linear-gradient(
    135deg,
    rgba(40, 167, 69, 0.2) 0%,
    rgba(32, 201, 151, 0.15) 100%
  );
  border: 1px solid rgba(40, 167, 69, 0.4);
  border-radius: 6px;
  color: #28a745;
  font-size: 11px;
  font-weight: 600;
}

// Amount shortcuts
.amount-shortcuts-section {
  margin-bottom: 20px;
}

.shortcuts-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.shortcut-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  padding: 10px 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 40px;

  &:hover {
    background: rgba(var(--theme-secondary-rgb), 0.1);
    border-color: rgba(var(--theme-secondary-rgb), 0.3);
  }

  &.active {
    background: linear-gradient(
      135deg,
      rgba(var(--theme-secondary-rgb), 0.2),
      rgba(255, 153, 0, 0.2)
    );
    border-color: var(--theme-secondary);
    color: #ffffff;
  }

  @media (max-width: 360px) {
    font-size: 12px;
    padding: 8px 4px;
  }
}

// Continue button
.continue-btn {
  width: 100%;
  background: var(--theme-gradient-primary);
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(var(--theme-primary-rgb), 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Back button
.back-btn {
  display: block;
  width: 100%;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  padding: 10px;
  margin-top: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }
}

// Transfer step - Compact countdown (horizontal layout)
.countdown-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(var(--theme-secondary-rgb), 0.1);
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.3s ease;

  &.warning {
    background: rgba(220, 53, 69, 0.15);
    animation: pulse 1s infinite;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.countdown-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.countdown-time {
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  flex-shrink: 0;

  .warning & {
    color: #dc3545;
  }
}

.countdown-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  white-space: nowrap;
}

// Transfer amount section
// Warning banner
.deposit-warning-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(220, 53, 69, 0.15);
  border: 1.5px solid rgba(220, 53, 69, 0.5);
  border-radius: 8px;
  margin-bottom: 12px;
  animation: warningPulse 2s ease-in-out infinite;
}

.deposit-warning-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.deposit-warning-text {
  color: #ff4d4d;
  font-size: 15px;
  font-weight: 350;
  line-height: 1.4;
}

@keyframes warningPulse {
  0%,
  100% {
    border-color: rgba(220, 53, 69, 0.5);
    background: rgba(220, 53, 69, 0.15);
  }
  50% {
    border-color: rgba(220, 53, 69, 0.9);
    background: rgba(220, 53, 69, 0.25);
  }
}

.transfer-amount-section {
  margin-bottom: 10px;
}

.amount-display {
  background: rgba(var(--theme-secondary-rgb), 0.1);
  border: 2px solid rgba(var(--theme-secondary-rgb), 0.3);
  border-radius: 10px;
  padding: 6px;
  text-align: center;
}

.amount-value {
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
}

// QR Code Payment Section
.qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 18px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  backdrop-filter: blur(10px);
  margin-bottom: 12px;
}

.qr-hint-top {
  color: #ffffff;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  margin: 0;
}

.qr-image-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 12px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.qr-image {
  display: block;
  width: 260px;
  max-width: 100%;
  height: auto;

  @media (max-width: 360px) {
    width: 220px;
  }
}

.save-qr-btn {
  width: 100%;
  max-width: 280px;
  background: transparent;
  border: 1.5px solid rgba(var(--theme-secondary-rgb), 0.6);
  border-radius: 10px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--theme-secondary-rgb), 0.12);
    border-color: var(--theme-secondary);
  }

  &:active {
    transform: scale(0.98);
  }
}

.qr-hint-bottom {
  color: #ffc107;
  font-size: 12px;
  text-align: center;
  margin: 0;
  line-height: 1.4;
}

// Selected promotion section
.selected-promo-section {
  margin-bottom: 10px;
}

.selected-promo-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(var(--theme-secondary-rgb), 0.05);
  border: 1px solid rgba(var(--theme-secondary-rgb), 0.2);
  border-radius: 8px;
}

.promo-info {
  flex: 1;
  min-width: 0;

  .promo-title {
    font-size: 11px;
    margin-bottom: 2px;
  }

  .promo-teaser {
    font-size: 10px;
    margin: 0;
  }
}

.promo-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.change-promo-btn {
  background: rgba(23, 162, 184, 0.2);
  border: 1px solid rgba(23, 162, 184, 0.4);
  border-radius: 5px;
  color: #17a2b8;
  font-size: 11px;
  padding: 5px 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(23, 162, 184, 0.3);
  }
}

.cancel-promo-btn {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 5px;
  color: #dc3545;
  font-size: 11px;
  padding: 5px 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(220, 53, 69, 0.2);
  }
}

// Bank sections
.bank-section {
  margin-bottom: 8px;

  &.compact {
    margin-bottom: 16px;

    .section-label {
      font-size: 11px;
      margin-bottom: 6px;
    }

    .bank-card {
      padding: 10px;
    }

    .bank-name {
      font-size: 11px;
    }

    .account-name {
      font-size: 11px;
    }

    .account-number {
      font-size: 12px;
    }
  }
}

.bank-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;

  &.company-bank {
    background: rgba(var(--theme-secondary-rgb), 0.05);
    border-color: rgba(var(--theme-secondary-rgb), 0.2);
  }

  &.user-bank {
    background: rgba(102, 126, 234, 0.08);
    border-color: rgba(102, 126, 234, 0.25);
  }
}

.bank-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--theme-gradient-primary);
  border-radius: 6px;
  color: white;
}

.bank-logo {
  flex-shrink: 0;
}

.logo-img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: contain;
}

.bank-info {
  flex: 1;
  min-width: 0;
}

.bank-name {
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 2px;
}

.account-name {
  color: #fff;
  font-size: 12px;
  margin-bottom: 3px;
}

.account-number {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-family: 'Courier New', monospace;
}

.account-number-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: rgba(var(--theme-secondary-rgb), 0.1);
  border: 1px solid rgba(var(--theme-secondary-rgb), 0.3);
  border-radius: 5px;
  color: #ffffff;
  font-size: 10px;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(var(--theme-secondary-rgb), 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.copied {
    background: rgba(40, 167, 69, 0.2);
    border-color: rgba(40, 167, 69, 0.4);
    color: #28a745;
  }
}

// Transfer arrow
.transfer-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 0;
}

.arrow-icon {
  font-size: 20px;
}

.arrow-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 10px;
}

// Warning section
.warning-section {
  display: flex;
  gap: 8px;
  padding: 10px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.2);
  border-radius: 8px;
  margin-bottom: 12px;
}

.warning-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.warning-content {
  flex: 1;
}

.warning-title {
  color: #ffc107;
  font-size: 12px;
  font-weight: 500;
  margin: 0 0 4px 0;
}

.warning-list {
  margin: 0;
  padding-left: 16px;

  li {
    color: rgba(255, 255, 255, 0.7);
    font-size: 11px;
    margin-bottom: 3px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Action buttons
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.confirm-transfer-btn {
  width: 100%;
  background: linear-gradient(135deg, #28a745, #20c997);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 10px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
  }
}

.cancel-btn {
  width: 100%;
  background: transparent;
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 8px;
  color: #dc3545;
  font-size: 12px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(220, 53, 69, 0.1);
  }
}

// Transaction section
.transaction-section {
  margin-top: 16px;
}

.section-header {
  margin-bottom: 12px;
}

.section-title {
  color: #ffffff;
  font-size: 16px;
  font-weight: 400;
  margin: 0;
}

.transaction-list {
  min-height: 80px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
}

.empty-icon {
  font-size: 32px;
  margin-bottom: 8px;
  opacity: 0.5;
}

.empty-text {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  margin: 0;
}

.transaction-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.transaction-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.transaction-index {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--theme-secondary-rgb), 0.2);
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 600;
  color: #ffffff;
  flex-shrink: 0;
}

.transaction-info {
  flex: 1;
  min-width: 0;

  .transaction-amount {
    color: #fff;
    font-size: 0.85rem;
    font-weight: 600;
  }

  .transaction-date {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.7rem;
  }
}

.transaction-status {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  flex-shrink: 0;

  &.status-success {
    background: rgba(40, 167, 69, 0.2);
    color: #4ade80;
  }

  &.status-warning {
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
  }

  &.status-danger {
    background: rgba(220, 53, 69, 0.2);
    color: #ef4444;
  }
}

// Promotion Selection Section (Step 1)
.promotion-selection-section {
  margin-bottom: 20px;
}

.promo-select-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba(var(--theme-secondary-rgb), 0.05);
  border: 2px dashed rgba(var(--theme-secondary-rgb), 0.3);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--theme-secondary-rgb), 0.1);
    border-color: rgba(var(--theme-secondary-rgb), 0.5);
  }
}

.promo-btn-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.promo-btn-icon {
  font-size: 24px;
}

.promo-btn-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.promo-btn-title {
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
}

.promo-btn-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
}

.promo-btn-arrow {
  color: #ffffff;
  font-size: 24px;
  font-weight: 300;
}

// Selected Promotion Display
.selected-promo-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  background: rgba(var(--theme-secondary-rgb), 0.1);
  border: 1px solid rgba(var(--theme-secondary-rgb), 0.3);
  border-radius: 10px;
}

.promo-display-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.promo-display-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.promo-display-info {
  flex: 1;
  min-width: 0;
}

.promo-display-title {
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  margin: 0 0 3px 0;
}

.promo-display-teaser {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  margin: 0 0 2px 0;
}

.promo-display-min {
  color: rgba(255, 255, 255, 0.5);
  font-size: 10px;
  margin: 0;
}

.promo-display-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}

.promo-change-btn {
  background: rgba(23, 162, 184, 0.2);
  border: 1px solid rgba(23, 162, 184, 0.4);
  border-radius: 6px;
  color: #17a2b8;
  font-size: 11px;
  padding: 5px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(23, 162, 184, 0.3);
  }
}

.promo-cancel-btn {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 6px;
  color: #dc3545;
  font-size: 11px;
  padding: 5px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(220, 53, 69, 0.2);
  }
}

// Promotion Modal
.promo-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out;

  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-end;
  }
}

.promo-modal {
  background: rgba(20, 20, 30, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    max-height: 85vh;
    border-radius: 16px 16px 0 0;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.promo-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.promo-modal-title {
  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  margin: 0;
}

.promo-modal-close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 6px;
  color: #fff;
  width: 32px;
  height: 32px;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

.promo-modal-content {
  padding: 16px 20px 20px 20px;
  overflow-y: auto;
  flex: 1;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--theme-secondary-rgb), 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(var(--theme-secondary-rgb), 0.5);
    }
  }
}

.promo-modal-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(var(--theme-secondary-rgb), 0.3);
    transform: translateX(4px);
  }

  &.no-promo-card {
    border-color: rgba(220, 53, 69, 0.3);
    background: rgba(220, 53, 69, 0.05);

    &:hover {
      background: rgba(220, 53, 69, 0.1);
      border-color: rgba(220, 53, 69, 0.5);
    }

    .promo-title {
      color: #dc3545;
    }
  }

  .promo-icon {
    font-size: 28px;
    flex-shrink: 0;
  }

  .promo-content {
    flex: 1;
    min-width: 0;
  }

  .promo-title {
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    margin: 0 0 4px 0;
  }

  .promo-teaser {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    margin: 0 0 4px 0;
  }

  .promo-min {
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
    margin: 0;
  }

  .promo-detail-btn {
    background: rgba(var(--theme-secondary-rgb), 0.15);
    border: 1px solid rgba(var(--theme-secondary-rgb), 0.4);
    border-radius: 6px;
    color: #ffffff;
    font-size: 10px;
    padding: 6px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;
    align-self: center;

    &:hover {
      background: rgba(var(--theme-secondary-rgb), 0.25);
      transform: scale(1.02);
    }

    &.event-detail-btn {
      background: rgba(255, 107, 138, 0.2);
      border-color: rgba(255, 107, 138, 0.5);
      color: #ff6b8a;

      &:hover {
        background: rgba(255, 107, 138, 0.3);
      }
    }

    &.register-detail-btn {
      background: rgba(0, 229, 255, 0.2);
      border-color: rgba(0, 229, 255, 0.5);
      color: #00e5ff;

      &:hover {
        background: rgba(0, 229, 255, 0.3);
      }
    }
  }

  // Event Promotion Card Modifier
  &.promo-event-card {
    position: relative;
    background: linear-gradient(
      135deg,
      rgba(255, 107, 138, 0.15) 0%,
      rgba(0, 191, 255, 0.1) 100%
    );
    border: 1px solid rgba(255, 107, 138, 0.4);
    overflow: hidden;
    animation: event-card-glow 2s ease-in-out infinite;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.05) 50%,
        transparent 70%
      );
      animation: shimmer 3s ease-in-out infinite;
      pointer-events: none;
    }

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(255, 107, 138, 0.25) 0%,
        rgba(0, 191, 255, 0.15) 100%
      );
      border-color: rgba(255, 107, 138, 0.6);
      transform: translateX(4px) scale(1.01);
      box-shadow: 0 4px 20px rgba(255, 107, 138, 0.3);
    }

    // Event Badge
    .event-badge {
      position: absolute;
      top: 0;
      right: 0;
      background: linear-gradient(135deg, #ff6b8a, #ff4d6d);
      padding: 4px 10px;
      border-radius: 0 10px 0 10px;
      z-index: 10;
      animation: badge-pulse 1.5s ease-in-out infinite;

      .event-badge-text {
        color: #fff;
        font-size: 10px;
        font-weight: 600;
      }
    }

    // Event Icon
    .promo-icon.event-icon {
      filter: drop-shadow(0 0 8px rgba(255, 107, 138, 0.6));
      animation: icon-float 2s ease-in-out infinite;
    }

    // Event Title - use solid color instead of gradient for better compatibility
    .promo-title.event-title {
      color: #ff6b8a;
      font-weight: 600;
    }

    // Event Time Info - Compact
    .event-time-info {
      margin: 6px 0 0 0;

      .event-countdown-compact {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: rgba(255, 107, 138, 0.2);
        border: 1px solid rgba(255, 107, 138, 0.3);
        border-radius: 4px;
        padding: 2px 8px;
        font-size: 11px;
        font-weight: 600;
        color: #ff6b8a;
      }

      .event-upcoming {
        color: rgba(255, 255, 255, 0.6);
        font-size: 10px;
        font-style: italic;
      }
    }
  }

  // Register Promotion Card Modifier
  &.promo-register-card {
    position: relative;
    background: linear-gradient(
      135deg,
      rgba(0, 229, 255, 0.15) 0%,
      rgba(124, 77, 255, 0.12) 50%,
      rgba(0, 191, 165, 0.1) 100%
    );
    border: 1px solid rgba(0, 229, 255, 0.5);
    overflow: hidden;
    animation: register-card-glow 2s ease-in-out infinite;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        transparent 20%,
        rgba(0, 229, 255, 0.08) 35%,
        rgba(124, 77, 255, 0.08) 50%,
        rgba(0, 229, 255, 0.08) 65%,
        transparent 80%
      );
      animation: register-shimmer 2.5s ease-in-out infinite;
      pointer-events: none;
    }

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(0, 229, 255, 0.25) 0%,
        rgba(124, 77, 255, 0.2) 50%,
        rgba(0, 191, 165, 0.15) 100%
      );
      border-color: rgba(0, 229, 255, 0.7);
      transform: translateX(4px) scale(1.02);
      box-shadow: 0 4px 24px rgba(0, 229, 255, 0.35);
    }

    // Register Badge
    .register-badge {
      position: absolute;
      top: 0;
      right: 0;
      display: flex;
      align-items: center;
      gap: 3px;
      background: linear-gradient(135deg, #00e5ff, #7c4dff);
      padding: 4px 12px;
      border-radius: 0 10px 0 10px;
      z-index: 10;
      animation: register-badge-pulse 1.5s ease-in-out infinite;

      .register-badge-icon {
        font-size: 10px;
        animation: register-star-spin 3s linear infinite;
      }

      .register-badge-text {
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.5px;
      }
    }

    // Register Icon
    .promo-icon.register-icon {
      filter: drop-shadow(0 0 10px rgba(0, 229, 255, 0.7));
      animation: register-icon-bounce 1.5s ease-in-out infinite;
    }

    // Register Title
    .promo-title.register-title {
      color: #00e5ff;
      font-weight: 700;
    }

    // Register Motivational Line
    .register-motivational {
      margin: 6px 0 0 0;

      .register-motivational-text {
        display: inline-flex;
        align-items: center;
        background: linear-gradient(
          135deg,
          rgba(0, 229, 255, 0.2),
          rgba(124, 77, 255, 0.15)
        );
        border: 1px solid rgba(0, 229, 255, 0.3);
        border-radius: 4px;
        padding: 2px 8px;
        font-size: 11px;
        font-weight: 600;
        color: #00e5ff;
        animation: motivational-glow 2s ease-in-out infinite;
      }
    }
  }
}

// Event card animations (global keyframes)
@keyframes event-card-glow {
  0%,
  100% {
    box-shadow: 0 0 8px rgba(255, 107, 138, 0.2);
  }
  50% {
    box-shadow: 0 0 16px rgba(255, 107, 138, 0.4),
      0 0 24px rgba(0, 191, 255, 0.2);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes badge-pulse {
  0%,
  100% {
    box-shadow: 0 2px 8px rgba(255, 77, 109, 0.4);
  }
  50% {
    box-shadow: 0 2px 12px rgba(255, 77, 109, 0.6);
  }
}

@keyframes icon-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

@keyframes icon-pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 8px rgba(255, 107, 138, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 16px rgba(255, 107, 138, 0.6);
  }
}

// Register card animations
@keyframes register-card-glow {
  0%,
  100% {
    box-shadow: 0 0 10px rgba(0, 229, 255, 0.25),
      0 0 20px rgba(124, 77, 255, 0.1);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 229, 255, 0.45),
      0 0 30px rgba(124, 77, 255, 0.25);
  }
}

@keyframes register-shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes register-badge-pulse {
  0%,
  100% {
    box-shadow: 0 2px 8px rgba(0, 229, 255, 0.4);
  }
  50% {
    box-shadow: 0 2px 16px rgba(0, 229, 255, 0.7),
      0 0 8px rgba(124, 77, 255, 0.4);
  }
}

@keyframes register-icon-bounce {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  25% {
    transform: translateY(-4px) scale(1.1);
  }
  50% {
    transform: translateY(0) scale(1);
  }
  75% {
    transform: translateY(-2px) scale(1.05);
  }
}

@keyframes register-star-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes motivational-glow {
  0%,
  100% {
    box-shadow: 0 0 4px rgba(0, 229, 255, 0.2);
  }
  50% {
    box-shadow: 0 0 10px rgba(0, 229, 255, 0.4);
  }
}

// Promotion Detail Modal
.promo-detail-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;

  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-end;
  }
}

.promo-detail-modal {
  background: rgba(20, 20, 30, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 450px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    max-height: 90vh;
    border-radius: 16px 16px 0 0;
  }

  // Event Detail Modal Modifier
  &.event-detail-modal {
    border-color: rgba(255, 107, 138, 0.4);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5),
      0 0 30px rgba(255, 107, 138, 0.2);
  }

  &.register-detail-modal {
    border-color: rgba(0, 229, 255, 0.4);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 30px rgba(0, 229, 255, 0.2),
      0 0 60px rgba(124, 77, 255, 0.1);
  }
}

.promo-detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.promo-detail-back {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  transition: all 0.2s ease;

  &:hover {
    color: #ffffff;
  }
}

.promo-detail-title {
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  flex: 1;
}

.promo-detail-body {
  padding: 24px 20px;
  overflow-y: auto;
  flex: 1;
  text-align: center;
}

.promo-detail-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.promo-detail-name {
  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 8px 0;
}

.promo-detail-teaser {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.promo-conditions {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
}

.condition-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
}

.condition-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
}

.condition-value {
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
}

.promo-full-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  line-height: 1.6;
  text-align: left;

  :deep(h4) {
    color: #ffffff;
    font-size: 15px;
    font-weight: 500;
    margin: 0 0 12px 0;
  }

  :deep(ul) {
    margin: 10px 0 16px 20px;
    padding: 0;

    li {
      margin-bottom: 6px;
      color: rgba(255, 255, 255, 0.8);

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  :deep(p) {
    margin-bottom: 12px;

    strong {
      color: #ffffff;
    }
  }
}

.promo-detail-footer {
  padding: 16px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  // Event footer modifier
  &.event-detail-footer {
    border-top-color: rgba(255, 107, 138, 0.3);
  }

  &.register-detail-footer {
    border-top-color: rgba(0, 229, 255, 0.3);
  }
}

.promo-select-btn {
  width: 100%;
  background: var(--theme-gradient-primary);
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  padding: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(var(--theme-primary-rgb), 0.4);
  }

  // Event select button modifier
  &.event-select-btn {
    background: linear-gradient(135deg, #ff6b8a, #ff4d6d);
    color: #fff;

    &:hover {
      box-shadow: 0 6px 20px rgba(255, 107, 138, 0.4);
    }
  }

  &.register-select-btn {
    background: linear-gradient(135deg, #00e5ff, #7c4dff);
    color: #fff;

    &:hover {
      box-shadow: 0 6px 24px rgba(0, 229, 255, 0.45);
    }
  }
}

// Event Detail Modal Styles
.event-detail-body {
  position: relative;
}

.event-detail-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 107, 138, 0.2),
    rgba(0, 191, 255, 0.1)
  );
  border: 1px solid rgba(255, 107, 138, 0.4);
  border-radius: 10px;
  animation: banner-pulse 2s ease-in-out infinite;

  .event-banner-icon {
    font-size: 18px;
    animation: spark 1s ease-in-out infinite;
  }

  .event-banner-text {
    color: #ff6b8a;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
}

@keyframes banner-pulse {
  0%,
  100% {
    box-shadow: 0 0 10px rgba(255, 107, 138, 0.2);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 107, 138, 0.4);
  }
}

@keyframes spark {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

.event-icon-glow {
  filter: drop-shadow(0 0 15px rgba(255, 107, 138, 0.5));
  animation: icon-float 2s ease-in-out infinite;
}

.event-name {
  color: #ff6b8a;
}

.register-icon-glow {
  filter: drop-shadow(0 0 15px rgba(0, 229, 255, 0.5))
    drop-shadow(0 0 30px rgba(124, 77, 255, 0.3));
  animation: register-icon-bounce 1.5s ease-in-out infinite;
}

.register-name {
  color: #00e5ff;
}

// Event Countdown in Detail Modal - Compact
.event-detail-countdown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 16px 0;
  padding: 10px 16px;
  background: rgba(255, 107, 138, 0.15);
  border: 1px solid rgba(255, 107, 138, 0.3);
  border-radius: 8px;

  .countdown-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
  }

  .countdown-value {
    color: #ff6b8a;
    font-size: 14px;
    font-weight: 600;
  }
}

// Step 3: Success State
.step-success {
  .success-card {
    text-align: center;
    padding: 40px 20px 32px;
  }

  .success-icon-wrapper {
    margin-bottom: 24px;
  }

  .success-icon {
    font-size: 64px;
    animation: successPulse 1.5s ease-in-out;
  }

  @keyframes successPulse {
    0%,
    100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.8;
    }
  }

  .success-message {
    margin-bottom: 28px;
  }

  .success-title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.5;
    margin: 0;

    @media (max-width: 480px) {
      font-size: 16px;
    }
  }

  .success-info {
    margin-bottom: 28px;
    padding: 16px;
    background: rgba(255, 193, 7, 0.1);
    border-radius: 8px;
    border: 1px solid rgba(255, 193, 7, 0.2);
  }

  .info-text {
    color: rgba(255, 255, 255, 0.8);
    font-size: 13px;
    line-height: 1.6;
    margin: 0;

    @media (max-width: 480px) {
      font-size: 12px;
    }
  }

  .contact-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 14px;
  }

  .line-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    background: #06c755; // LINE brand color
    border: none;
    border-radius: 10px;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: #05b04d;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(6, 199, 85, 0.4);
    }

    .line-icon {
      font-size: 18px;
    }

    .line-text {
      flex: 1;
    }
  }

  .exit-btn {
    width: 100%;
    background: transparent;
    border: 1px solid rgba(var(--theme-secondary-rgb), 0.3);
    border-radius: 10px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    padding: 14px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--theme-secondary-rgb), 0.1);
      border-color: var(--theme-secondary);
    }
  }
}
</style>

<style lang="scss">
// SweetAlert Congrats Popup (unscoped for SweetAlert)
.congrats-popup {
  background: linear-gradient(180deg, #064e3b 0%, #0d1421 100%) !important;
  border-radius: 16px !important;
  padding: 24px !important;
  max-width: 320px !important;
}

.congrats-content {
  text-align: center;
  padding: 16px 0;
}

.congrats-icon {
  font-size: 64px;
  margin-bottom: 16px;
  animation: swal-icon-bounce 0.6s ease-out;
}

.congrats-title {
  color: #fff !important;
  font-size: 22px !important;
  font-weight: 700 !important;
  margin: 0 0 12px 0 !important;
}

.congrats-amount {
  color: #4ade80 !important;
  font-size: 32px !important;
  font-weight: 700 !important;
  margin: 0 !important;
}

.congrats-btn {
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%) !important;
  border: none !important;
  border-radius: 24px !important;
  color: #fff !important;
  padding: 12px 48px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  margin-top: 8px !important;
  transition: transform 0.2s, box-shadow 0.2s !important;

  &:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(55, 65, 81, 0.4) !important;
  }
}

.congrats-close {
  color: rgba(255, 255, 255, 0.6) !important;
  position: absolute !important;
  top: 12px !important;
  right: 12px !important;

  &:hover {
    color: #fff !important;
  }
}

@keyframes swal-icon-bounce {
  0% {
    transform: scale(0);
  }

  50% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
}
</style>
